import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Link
} from '@mui/material';
import { MailOutline as MailIcon } from '@mui/icons-material';
import PasswordField from './PasswordField';

function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEmailNotVerified, setIsEmailNotVerified] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Check for success message from password reset
  useEffect(() => {
    const message = searchParams.get('message');
    if (message) {
      setSuccessMessage(message);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setIsEmailNotVerified(false);
    setSuccessMessage('');

    try {
      const result = await login(formData);
      if (result.success) {
        navigate('/');
      } else {
        // Check if the error is specifically about email not being verified
        if (result.error && result.error.toLowerCase().includes('email not verified')) {
          setIsEmailNotVerified(true);
          setError('');
        } else {
          setError(result.error);
          setIsEmailNotVerified(false);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setIsEmailNotVerified(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            align="center"
            gutterBottom
            sx={{ mb: 3, fontWeight: 700 }}
          >
            Login to Your Account
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isEmailNotVerified && (
            <Alert
              severity="warning"
              icon={<MailIcon />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Email Not Verified
              </Typography>
              <Typography variant="body2">
                Please check your inbox for a verification email and click the verification link to activate your account.
                If you didn't receive the email, please check your spam folder.
              </Typography>
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email address"
              margin="normal"
              autoComplete="email"
              autoFocus
            />

            <PasswordField
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Enter your password"
              margin="normal"
              autoComplete="current-password"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </Box>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <Link component={RouterLink} to="/forgot-password" underline="hover">
                Forgot your password?
              </Link>
            </Typography>
            <Typography variant="body2">
              Don't have an account?{' '}
              <Link component={RouterLink} to="/create-account" underline="hover" fontWeight={600}>
                Create Account
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default Login;
